import { useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useMemo, type CSSProperties } from "react";
import { navTabsListStyles } from "./navTabsLayout";

function mobileNavTabsListBase(listOverrides?: CSSProperties) {
  return {
    display: "grid" as const,
    gridTemplateColumns: "1fr",
    width: "100%" as const,
    maxWidth: "100%",
    minWidth: 0,
    alignItems: "stretch" as const,
    boxShadow: "none" as const,
    gap: 0,
    ...listOverrides,
  };
}

/**
 * Below Mantine `sm`: stack nav tabs full-width and allow labels to wrap.
 * Desktop keeps `navTabsListStyles` (inline grid, equal columns).
 */
export function useNavTabsStackLayout(columnCount: number, listOverrides?: CSSProperties) {
  const theme = useMantineTheme();
  const matchesNarrow = useMediaQuery(
    `(max-width: calc(${theme.breakpoints.sm} - 0.0625em))`,
    false,
    { getInitialValueInEffect: true }
  );
  const isMobile = matchesNarrow === true;

  const tabsListStyles = useMemo(
    () =>
      isMobile
        ? { list: mobileNavTabsListBase(listOverrides) }
        : navTabsListStyles(columnCount, listOverrides),
    [isMobile, columnCount, listOverrides]
  );

  const tabsRootStyles = useMemo(
    () => ({
      tab: {
        whiteSpace: (isMobile ? "normal" : "nowrap") as "normal" | "nowrap",
        ...(isMobile ? { textAlign: "center" as const } : {}),
      },
    }),
    [isMobile]
  );

  return { isMobile, tabsListStyles, tabsRootStyles };
}
